'use client'

import { Button } from '@/components/ui/button'
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle
} from '@/components/ui/resizable-navbar'
import { LoginDialog } from '@/components/auth/login-dialog'
import { LanguageSwitcher } from '@/components/language-switcher'
import { ThemeSwitcher } from '@/components/theme-switcher'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useAuth } from '@/hooks/useAuth'
import Logo from '../logo'

export function PublicNavbar() {
  const t = useTranslations('nav')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { user, isLoading } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check initial state

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = useMemo<
    Array<{ name: string; link: string; locale?: 'en' }>
  >(
    () => [
      { name: t('stories'), link: '/read' },
      { name: t('dictionary'), link: '/dict' },
      { name: t('grammar'), link: '/grammar', locale: 'en' },
      { name: t('blog'), link: '/blog' }
    ],
    [t]
  )

  const showLogin = !isLoading && !user

  return (
    <Navbar>
      <NavBody>
        <Link
          href="/"
          className="relative z-20 mr-4 flex items-center space-x-2 px-2 py-1 text-sm font-normal text-black dark:text-white"
        >
          <Logo
            className="-my-2 h-14 w-14 text-amber-950 dark:text-amber-100"
            aria-label="Miaozi"
          />
          <span className="text-base font-medium">Miaozi</span>
        </Link>
        <NavItems items={navItems} />
        <div className="relative z-20 ml-auto flex items-center gap-2">
          <AnimatePresence>
            {!isScrolled && (
              <motion.div
                initial={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2 overflow-hidden"
              >
                <ThemeSwitcher />
                <LanguageSwitcher />
              </motion.div>
            )}
          </AnimatePresence>
          {showLogin && (
            <LoginDialog>
              <Button variant="ghost">{t('login')}</Button>
            </LoginDialog>
          )}
          <Button asChild>
            <Link href="/home">{t('launchApp')}</Link>
          </Button>
        </div>
      </NavBody>
      <MobileNav>
        <MobileNavHeader>
          <Link
            href="/"
            className="text-lg font-semibold tracking-tight text-black dark:text-white"
          >
            Miaozi
          </Link>
          <MobileNavToggle
            isOpen={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
        </MobileNavHeader>
        <MobileNavMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        >
          {navItems.map((item) => (
            <Link
              key={item.link}
              href={item.link}
              locale={item.locale}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex gap-2">
              <ThemeSwitcher />
              <LanguageSwitcher />
            </div>
            {showLogin && (
              <LoginDialog>
                <Button variant="ghost" className="w-full">
                  {t('login')}
                </Button>
              </LoginDialog>
            )}
            <Button asChild className="w-full">
              <Link href="/home">{t('launchApp')}</Link>
            </Button>
          </div>
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
  )
}
